import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  BookOpen,
  Package,
  Download,
  Settings,
  CreditCard,
  Menu,
  X
} from 'lucide-react';
import Navigation from '@/components/Navigation';

interface SidebarLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, icon, label, isActive }) => {
  return (
    <Link
      to={to}
      className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
        isActive
          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
          : 'text-gray-400 hover:text-white hover:bg-slate-800/60'
      }`}
    >
      <span className={`transition-transform duration-200 ${isActive ? '' : 'group-hover:scale-110'}`}>
        {icon}
      </span>
      <span className="font-medium">{label}</span>
    </Link>
  );
};

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    {
      to: '/dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      label: 'Dashboard'
    },
    {
      to: '/dashboard/courses',
      icon: <BookOpen className="w-5 h-5" />,
      label: 'My Courses'
    },
    {
      to: '/dashboard/bundles',
      icon: <Package className="w-5 h-5" />,
      label: 'My Bundles'
    },
    {
      to: '/dashboard/files',
      icon: <Download className="w-5 h-5" />,
      label: 'My Files'
    },
    {
      to: '/dashboard/settings',
      icon: <Settings className="w-5 h-5" />,
      label: 'Account Settings'
    },
    {
      to: '/dashboard/billing',
      icon: <CreditCard className="w-5 h-5" />,
      label: 'Billing & Subscriptions'
    }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
      {/* Top Navigation - Fixed */}
      <Navigation />
      
      {/* Main Container - Below nav with proper top padding, takes remaining height */}
      <div className="flex flex-1 overflow-hidden pt-[72px]">
        {/* Desktop Sidebar - Fixed height, scrollable content */}
        <aside className="hidden lg:flex lg:flex-shrink-0 w-72 border-r border-white/10 bg-black/20 backdrop-blur-xl">
          <div className="flex flex-col w-full overflow-y-auto">
            <div className="flex-shrink-0 pt-8 pb-4 px-4">
              <div className="mb-6 px-4">
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Dashboard
                </h2>
              </div>
              <div className="space-y-2">
                {navigationItems.map((item) => (
                  <SidebarLink
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    isActive={location.pathname === item.to}
                  />
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 z-40 lg:hidden"
            onClick={toggleMobileMenu}
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </div>
        )}

        {/* Mobile Sidebar */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 border-r border-white/10 transform transition-transform duration-300 ease-in-out lg:hidden ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-4 border-b border-white/10 mt-[72px]">
            <h2 className="text-lg font-semibold text-white">Dashboard Menu</h2>
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-slate-800/60 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex flex-col pt-8 px-4 space-y-2 overflow-y-auto">
            {navigationItems.map((item) => (
              <div key={item.to} onClick={toggleMobileMenu}>
                <SidebarLink
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  isActive={location.pathname === item.to}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Area - Scrollable only */}
        <main className="flex-1 overflow-y-auto">
          {/* Mobile Menu Button */}
          <div className="lg:hidden fixed bottom-6 right-6 z-30">
            <button
              onClick={toggleMobileMenu}
              className="p-4 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/50 hover:shadow-xl hover:shadow-indigo-500/70 transition-all"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="py-8 px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
